import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Phone, CalendarCheck, TrendingUp, DollarSign, Megaphone,
  MessageSquare, Zap, ArrowRight, ChevronRight, CheckCircle,
  AlertCircle, Info, BarChart2
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar
} from "recharts";
import { LEADS, CAMPAIGNS, CONVERSATIONS, MONTHLY_DATA, AI_INSIGHTS, SITE_VISITS } from "@/data/dummyData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KPI_CARDS = [
  { label: "New Leads", value: 47, change: "+12%", up: true, icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
  { label: "Qualified Leads", value: 108, change: "+8%", up: true, icon: CheckCircle, color: "text-indigo-600", bg: "bg-indigo-50" },
  { label: "AI Calls", value: 28, change: "+23%", up: true, icon: Phone, color: "text-purple-600", bg: "bg-purple-50" },
  { label: "Site Visits", value: 9, change: "+3", up: true, icon: CalendarCheck, color: "text-orange-600", bg: "bg-orange-50" },
  { label: "Bookings", value: 4, change: "+1", up: true, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
  { label: "Revenue Pipeline", value: "₹24.6 Cr", change: "+18%", up: true, icon: DollarSign, color: "text-rose-600", bg: "bg-rose-50" },
];

const AGENT_STATUS = [
  { name: "Ads Agent", icon: Megaphone, status: "Active", campaigns: 4, processed: 1247, activity: "2 mins ago", color: "bg-blue-500", dot: "bg-blue-500" },
  { name: "WhatsApp Agent", icon: MessageSquare, status: "Active", campaigns: 4, processed: 847, activity: "30 sec ago", color: "bg-green-500", dot: "bg-green-500" },
  { name: "AI Calling Agent", icon: Phone, status: "Calling", campaigns: 3, processed: 234, activity: "Live now", color: "bg-purple-500", dot: "bg-purple-500" },
];

function AnimatedCounter({ target, duration = 1500 }) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (typeof target !== "number") return;
    let start = 0;
    const step = Math.ceil(target / (duration / 30));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setValue(target); clearInterval(timer); }
      else setValue(start);
    }, 30);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <>{typeof target === "number" ? value.toLocaleString() : target}</>;
}

const insightIcons = { opportunity: Info, warning: AlertCircle, success: CheckCircle };
const insightColors = { opportunity: "bg-blue-50 border-blue-100 text-blue-600", warning: "bg-yellow-50 border-yellow-100 text-yellow-600", success: "bg-emerald-50 border-emerald-100 text-emerald-600" };

const sourceColors = { meta: "bg-blue-100 text-blue-700", google: "bg-red-100 text-red-700", whatsapp: "bg-green-100 text-green-700" };

export default function Dashboard() {
  const [aiInsight, setAiInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const streamRef = useRef(null);

  const getAiInsight = async (lead) => {
    setSelectedLead(lead);
    setAiLoading(true);
    setAiInsight("");
    try {
      const res = await fetch(`${API}/ai/lead-insight`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lead_name: lead.name, stage: lead.stage, property_interest: lead.propertyInterest,
          budget: lead.budget, source: lead.source, score: lead.score,
          notes: lead.notes, days_in_pipeline: lead.daysInPipeline
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      streamRef.current = reader;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const text = line.slice(6);
            if (text === "[DONE]") { setAiLoading(false); return; }
            setAiInsight(p => p + text);
          }
        }
      }
    } catch (e) {
      setAiInsight("Unable to fetch insight. Please check your connection.");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Good morning, Rajesh</h2>
          <p className="text-slate-500 text-sm mt-0.5">Elite Estate · Tuesday, 23 January 2024</p>
        </div>
        <Link
          to="/leads"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          data-testid="dashboard-view-leads-btn"
        >
          View Leads <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-cards">
        {KPI_CARDS.map((kpi, i) => (
          <motion.div
            key={kpi.label}
            className="kpi-card"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className={`w-8 h-8 ${kpi.bg} rounded-lg flex items-center justify-center mb-3`}>
              <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
            </div>
            <p className="text-xs text-slate-500 mb-1 font-medium">{kpi.label}</p>
            <p className={`font-heading text-xl font-bold ${kpi.color}`}>
              {typeof kpi.value === "number" ? <AnimatedCounter target={kpi.value} /> : kpi.value}
            </p>
            <p className={`text-xs font-semibold mt-1 ${kpi.up ? "text-emerald-600" : "text-red-500"}`}>{kpi.change} today</p>
          </motion.div>
        ))}
      </div>

      {/* AI Outreach Engine */}
      <motion.div
        className="bg-white rounded-xl border border-slate-200 p-6"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        data-testid="ai-outreach-engine"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            <h3 className="font-heading font-semibold text-slate-900">AI Outreach Engine</h3>
          </div>
          <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded-full font-semibold">All Systems Active</span>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-0">
          {AGENT_STATUS.map((agent, i) => (
            <div key={agent.name} className="flex items-center w-full">
              <motion.div
                className="flex-1 bg-slate-50 rounded-xl p-4 border border-slate-100"
                whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}
                data-testid={`agent-card-${i}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 ${agent.color} rounded-lg flex items-center justify-center`}>
                    <agent.icon className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{agent.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className={`w-1.5 h-1.5 ${agent.dot} rounded-full animate-pulse`} />
                      <p className="text-xs text-slate-500">{agent.status}</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-slate-400">Campaigns</p>
                    <p className="text-sm font-bold text-slate-800">{agent.campaigns}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Processed</p>
                    <p className="text-sm font-bold text-slate-800">{agent.processed.toLocaleString()}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2 pt-2 border-t border-slate-100">{agent.activity}</p>
              </motion.div>

              {i < AGENT_STATUS.length - 1 && (
                <div className="relative mx-3 w-8 flex-shrink-0 hidden md:flex flex-col items-center">
                  <div className="w-full h-0.5 bg-slate-200" />
                  <div className="absolute flow-dot w-2 h-2 bg-blue-400 rounded-full top-1/2 -translate-y-1/2" />
                  <div className="absolute flow-dot-delay w-2 h-2 bg-blue-300 rounded-full top-1/2 -translate-y-1/2" />
                </div>
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* Charts + Campaigns Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lead Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6" data-testid="lead-trend-chart">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-heading font-semibold text-slate-900">Lead & Booking Trend</h3>
            <span className="text-xs text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">Last 6 months</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={MONTHLY_DATA} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="leadsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }} />
              <Area type="monotone" dataKey="leads" stroke="#2563eb" strokeWidth={2} fill="url(#leadsGrad)" name="Leads" />
              <Area type="monotone" dataKey="bookings" stroke="#10b981" strokeWidth={2} fill="none" name="Bookings" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Active Campaigns */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="active-campaigns-widget">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-slate-900">Active Campaigns</h3>
            <Link to="/campaigns" className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1">
              View All <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-3">
            {CAMPAIGNS.filter(c => c.status === "active").slice(0, 4).map(camp => (
              <div key={camp.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                <div className={`w-2 h-2 rounded-full ${camp.type === "meta" ? "bg-blue-500" : camp.type === "google" ? "bg-red-500" : camp.type === "whatsapp" ? "bg-green-500" : "bg-purple-500"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate">{camp.property}</p>
                  <p className="text-xs text-slate-500">{camp.leads} leads · CPL ₹{camp.cpl}</p>
                </div>
                <span className="text-xs font-bold text-blue-600">{camp.aiScore}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Leads + Site Visits Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Leads */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6" data-testid="recent-leads-widget">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-slate-900">Recent Leads</h3>
            <Link to="/leads" className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1">
              View Pipeline <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {LEADS.slice(0, 6).map(lead => (
              <div
                key={lead.id}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer group"
                onClick={() => getAiInsight(lead)}
                data-testid={`lead-row-${lead.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {lead.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-800">{lead.name}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${sourceColors[lead.source] || "bg-slate-100 text-slate-600"}`}>
                      {lead.source}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{lead.propertyInterest} · {lead.budget}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-bold text-blue-600">{lead.score}</p>
                  <p className="text-xs text-slate-400">{lead.lastActivity}</p>
                </div>
                <div className="w-2 h-2 rounded-full bg-blue-200 group-hover:bg-blue-500 transition-colors" />
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Site Visits + AI Insights */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="site-visits-widget">
            <h3 className="font-heading font-semibold text-slate-900 mb-3">Upcoming Visits</h3>
            <div className="space-y-2">
              {SITE_VISITS.slice(0, 3).map(v => (
                <div key={v.id} className="p-3 rounded-lg bg-slate-50">
                  <p className="text-xs font-semibold text-slate-800">{v.leadName}</p>
                  <p className="text-xs text-slate-500">{v.property}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-blue-600 font-medium">{v.time}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${v.status === "confirmed" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`}>
                      {v.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="ai-insights-widget">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-blue-600" />
              <h3 className="font-heading font-semibold text-slate-900">AI Insights</h3>
            </div>
            <div className="space-y-2">
              {AI_INSIGHTS.slice(0, 2).map(insight => {
                const Icon = insightIcons[insight.type];
                return (
                  <div key={insight.id} className={`p-3 rounded-lg border ${insightColors[insight.type]}`}>
                    <div className="flex items-start gap-2">
                      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold">{insight.title}</p>
                        <p className="text-xs opacity-80 mt-0.5 leading-relaxed">{insight.message.substring(0, 80)}...</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* AI Lead Insight Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedLead(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6"
            onClick={e => e.stopPropagation()}
            data-testid="ai-insight-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-blue-600" />
                <h3 className="font-heading font-semibold text-slate-900">AI Lead Insight</h3>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">{selectedLead.avatar}</div>
                <p className="text-sm font-semibold text-slate-800">{selectedLead.name}</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 min-h-32">
              {aiLoading && !aiInsight && (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  Generating AI insight...
                </div>
              )}
              {aiInsight && <p className="text-sm text-slate-700 ai-stream">{aiInsight}</p>}
              {aiLoading && aiInsight && <span className="inline-block w-1 h-4 bg-blue-600 animate-pulse ml-0.5" />}
            </div>
            <button
              className="mt-4 w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2 rounded-lg text-sm transition-colors"
              onClick={() => setSelectedLead(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
